import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Phone, Mail, Monitor } from 'lucide-react';
import './Video.css';
import { useSurveySrv } from "pages/Survey/useSurveySrv";
import { handleErrorNavigation } from "pages/ErrorPage/ErrorHandler";
import { useTranslation } from "react-i18next";
import { v4 as uuidv4 } from 'uuid';
import { usePageTitle } from "helpers/usePageTitle";

const DEFAULT_BRAND_COLORS = {
    primary: '#2563EB',
    secondary: '#4F46E5',
    accent: '#10B981',
    background: '#F9FAFB',
    text: '#111827'
};

const getQueryParam = (k: string) => {
    try {
        return new URLSearchParams(window.location.search).get(k);
    } catch {
        return null;
    }
};

type VideoCampaignAction = {
    VideoCampaignActionDetailID?: string;
    ActionType?: number;
    ActionURL?: string;
    ActionText?: string;
    ActionSubject?: string;
    ActionContent?: string;
    ActionTarget?: string;
};

type VideoCampaign = {
    CampaignTitle?: string;
    CampaignSubtitle?: string;
    CampaignUrl?: string;
    Language?: number;
    Actions?: VideoCampaignAction[];
};

type VideoEventRecord = {
    SessionIdentifier: string;
    eventType: string;
    timestamp: string;
    position: number;
    totalWatchTime: number;
    device: { ua: string };
    IsViewedFully: boolean;
    [key: string]: any;
};

const VideoPlayer = () => {
    const params = useParams();
    const { t } = useTranslation();
    usePageTitle("VIDEO.PAGE_TITLE", `Video - ${process.env.REACT_APP_PRODUCT}`);
    const navigate = useNavigate();
    const { getVideoCampaignData, sendAnalytics } = useSurveySrv();

    const campaignName =
        params.campaignName || getQueryParam('campaignName') || null;
    const userRegistrationCode = params.userRegistrationCode || getQueryParam('userRegistrationCode') || null;
    const [campaign, setCampaign] = useState<VideoCampaign | null>(null);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        const fetchCampaignData = async () => {
            setIsLoading(true);
            try {
                const sessionIdKey = `videoSession_${campaignName}_${userRegistrationCode}`;
                let isNewSession = false;
                try {
                    let sid = sessionStorage.getItem(sessionIdKey);
                    if (!sid) {
                        sid = uuidv4();
                        try {
                            sessionStorage.setItem(sessionIdKey, sid);
                        } catch (_) {
                            // ignore storage errors
                        }
                        isNewSession = true;
                        console.log('New session created for video:', sid);
                    } else {
                        console.log('Existing session found for video:', sid);
                    }
                    sessionIdRef.current = sid;
                } catch (e) {
                    if (!sessionIdRef.current) sessionIdRef.current = uuidv4();
                    isNewSession = true;
                }

                const campaignSessionKey = `videoCampaign_${campaignName}_${userRegistrationCode}`;
                const sessionData = sessionStorage.getItem(campaignSessionKey);

                let apiData;
                if (sessionData && !isNewSession) {
                    apiData = JSON.parse(sessionData);
                    console.log('Using campaign data from session storage (same session)');
                } else {
                    const res: any = await getVideoCampaignData(campaignName, userRegistrationCode);
                    apiData = res?.data ?? {};
                    try {
                        sessionStorage.setItem(campaignSessionKey, JSON.stringify(apiData));
                    } catch (_) {
                        // ignore storage errors
                    }
                    console.log(isNewSession ? 'Fetched campaign data for new session and stored' : 'Fetched campaign data and stored');
                }

                // Initialize IsViewedFully tracking for this session
                const isViewedFullyKey = `videoIsViewedFully_${campaignName}_${userRegistrationCode}_${sessionIdRef.current}`;
                try {
                    const storedIsViewedFully = sessionStorage.getItem(isViewedFullyKey);
                    if (storedIsViewedFully !== null) {
                        isViewedFullyRef.current = storedIsViewedFully === 'true';
                    } else {
                        isViewedFullyRef.current = false;
                        sessionStorage.setItem(isViewedFullyKey, 'false');
                    }
                } catch (_) {
                    isViewedFullyRef.current = false;
                }

                if (apiData && apiData.CampaignDetail && Array.isArray(apiData.CampaignDetail.Actions)) {
                    console.log('Campaign data:', apiData);
                    setCampaign(apiData.CampaignDetail);
                } else {
                    const err = new Error("Invalid or missing campaign data.");
                    const defaultNavParams = {
                        state: {
                            errorCode: "",
                            errorTitle: err.message,
                            errorDescription: ""
                        }
                    };
                    navigate('/not-found', defaultNavParams);
                }
            } catch (error) {
                console.error("Error fetching campaign data:", error);
                const defaultNavParams = {
                    state: {
                        errorCode: "",
                        errorTitle: (error as any)?.data?.Message,
                        errorDescription: ""
                    }
                };
                navigate('/not-found', defaultNavParams);
            } finally {
                setIsLoading(false);
            }
        };

        fetchCampaignData();
    }, []);

    const [videoState, setVideoState] = useState<{ isPlaying: boolean; currentTime: number; duration: number; progress: number }>({ isPlaying: false, currentTime: 0, duration: 0, progress: 0 });

    const videoRef = useRef<HTMLVideoElement | null>(null);
    const lastPlayTimestampRef = useRef<number>(0);
    const accumulatedWatchTimeRef = useRef<number>(0);
    const analyticsBufferRef = useRef<VideoEventRecord[]>([]);
    const seenSecondsSetRef = useRef<Set<number>>(new Set());
    const seenIntervalIdRef = useRef<number | null>(null);
    const hasPlaybackStartedRef = useRef<boolean>(false);
    const periodicHeartbeatIdRef = useRef<number | null>(null);
    const sessionIdRef = useRef<string | null>(null);
    const isViewedFullyRef = useRef<boolean>(false);

    const makeKey = (prefix: string) => `${prefix}_${campaignName}_${userRegistrationCode}`;

    const genSid = () => {
        const key = makeKey('videoSession');
        let id = sessionStorage.getItem(key);
        if (!id) {
            id = uuidv4(); // generate a new unique session ID
            sessionStorage.setItem(key, id);
        }
        return id;
    };

    const ensureSessionId = () => {
        if (sessionIdRef.current) return sessionIdRef.current;
        try {
            const key = makeKey('videoSession');
            const navType = (performance && (performance.getEntriesByType ? (performance.getEntriesByType('navigation')[0] as any)?.type : (performance as any).navigation?.type)) || '';
            let sid = sessionStorage.getItem(key);
            if (!sid || navType === 'reload') {
                sid = genSid();
                try { sessionStorage.setItem(key, sid); } catch { }
            }
            sessionIdRef.current = sid;
            return sid;
        } catch {
            sessionIdRef.current = genSid();
            return sessionIdRef.current;
        }
    };

    const getIsViewedFully = () => {
        return isViewedFullyRef.current;
    };

    const setIsViewedFully = (value: boolean) => {
        // Only allow setting to true, never back to false
        if (value === true && !isViewedFullyRef.current) {
            isViewedFullyRef.current = true;
            const isViewedFullyKey = `videoIsViewedFully_${campaignName}_${userRegistrationCode}_${sessionIdRef.current}`;
            try {
                sessionStorage.setItem(isViewedFullyKey, 'true');
                console.log('IsViewedFully set to true for session:', sessionIdRef.current);
            } catch (_) {
                // ignore storage errors
            }
        }
    };

    const buildPayload = (extra: Record<string, any> = {}) => {
        const durationSec = getDurationSeconds();
        const totalTime = Math.floor(durationSec / 60) + Math.floor(durationSec % 60) / 100
        const totalWatch = Math.floor((accumulatedWatchTimeRef.current || 0) / 60) + Math.floor((accumulatedWatchTimeRef.current || 0) % 60) / 100
        return {
            VideoCampaignAuditID: 0,
            campaignName: campaignName || '',
            SessionIdentifier: sessionIdRef.current,
            MemberRegistrationCode: userRegistrationCode || '',
            TotalTime: totalTime,
            TotalWatchTime: totalWatch,
            IsViewedFully: getIsViewedFully(),
            ...extra
        } as Record<string, any>;
    };

    const getDurationSeconds = useCallback(() => {
        const elDur = videoRef.current?.duration;
        if (typeof elDur === 'number' && !isNaN(elDur) && elDur > 0) return elDur;
        return videoState.duration || 0;
    }, [videoState.duration]);

    const trackEvent = useCallback((eventType: string, data: Record<string, any> = {}) => {
        ensureSessionId();
        const record: VideoEventRecord = {
            SessionIdentifier: sessionIdRef.current!,
            eventType,
            timestamp: new Date().toISOString(),
            position: videoRef.current?.currentTime || 0,
            totalWatchTime: Math.floor((accumulatedWatchTimeRef.current || 0) / 60) + Math.floor((accumulatedWatchTimeRef.current || 0) % 60) / 100,
            device: { ua: navigator.userAgent },
            IsViewedFully: getIsViewedFully(),
            ...data
        };
        analyticsBufferRef.current.push(record);
        return record;
    }, []);

    const sendAnalyticsBatch = useCallback(async (extra: Record<string, any> = {}) => {
        // If video completed, set IsViewedFully to true
        if (extra.completed) {
            setIsViewedFully(true);
        }

        const payload = buildPayload(extra);

        try {
            sendAnalytics(payload);
            analyticsBufferRef.current = [];
            return true;
        } catch (e) {
            handleErrorNavigation(e, navigate, t);
            return false;
        }
    }, [campaign?.Language, campaignName]);

    useEffect(() => {
        periodicHeartbeatIdRef.current = window.setInterval(() => {
            if (lastPlayTimestampRef.current && videoRef.current && !videoRef.current.paused) {
                const elapsed = (Date.now() - lastPlayTimestampRef.current) / 1000;
                accumulatedWatchTimeRef.current += elapsed;
                lastPlayTimestampRef.current = Date.now();
            }

            trackEvent('periodic_heartbeat', { interval: 30 });
            sendAnalyticsBatch({ trigger: 'periodic_30s' }).catch(() => { });
        }, 30000) as unknown as number;

        return () => {
            if (periodicHeartbeatIdRef.current) {
                window.clearInterval(periodicHeartbeatIdRef.current!);
                periodicHeartbeatIdRef.current = null;
            }
        };
    }, [trackEvent, sendAnalyticsBatch]);

    useEffect(() => {
        const handleBeforeUnload = (e: BeforeUnloadEvent) => {
            if (lastPlayTimestampRef.current && videoRef.current && !videoRef.current.paused) {
                const elapsed = (Date.now() - lastPlayTimestampRef.current) / 1000;
                accumulatedWatchTimeRef.current += elapsed;
                lastPlayTimestampRef.current = Date.now();
            }

            trackEvent('page_unload', { reason: 'browser_close' });
            ensureSessionId();

            const payload = buildPayload();

            try {
                sendAnalytics(payload);
            } catch (err) {
                console.log('sendAnalytics failed', err);
            }
        };

        const handleVisibilityChange = () => {
            if (document.hidden) {
                trackEvent('page_hidden');
                sendAnalyticsBatch({ trigger: 'page_hidden' }).catch(() => { });
            } else {
                trackEvent('page_visible');
            }
        };

        window.addEventListener('beforeunload', handleBeforeUnload);
        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [trackEvent, sendAnalyticsBatch, campaign?.Language, campaignName]);

    useEffect(() => {
        return () => {
            if (seenIntervalIdRef.current) window.clearInterval(seenIntervalIdRef.current);
        };
    }, []);

    const handlePlay = useCallback(() => {
        setVideoState((p) => ({ ...p, isPlaying: true }));
        lastPlayTimestampRef.current = Date.now();

        if (!hasPlaybackStartedRef.current) {
            hasPlaybackStartedRef.current = true;
            trackEvent('video_started');
        } else {
            trackEvent('video_play');
        }

        if (!seenIntervalIdRef.current) {
            seenIntervalIdRef.current = window.setInterval(() => {
                const v = videoRef.current;
                if (v && !v.paused) {
                    const sec = Math.floor(v.currentTime);
                    if (!seenSecondsSetRef.current.has(sec) && sec >= 0) {
                        seenSecondsSetRef.current.add(sec);
                        trackEvent('video_seen', { second: sec });
                    }
                }
            }, 500) as unknown as number;
        }
    }, [trackEvent]);

    const flushOnStop = useCallback((extra = {}) => {
        if (lastPlayTimestampRef.current) {
            accumulatedWatchTimeRef.current += (Date.now() - lastPlayTimestampRef.current) / 1000;
            lastPlayTimestampRef.current = 0;
        }
        if (seenIntervalIdRef.current) {
            window.clearInterval(seenIntervalIdRef.current);
            seenIntervalIdRef.current = null;
        }
        return sendAnalyticsBatch(extra);
    }, [sendAnalyticsBatch]);

    const handlePause = useCallback(() => {
        setVideoState((p) => ({ ...p, isPlaying: false }));
        trackEvent('video_pause');
        flushOnStop({ trigger: 'pause' }).catch(() => { });
    }, [flushOnStop, trackEvent]);

    const handleTimeUpdate = useCallback(() => {
        const v = videoRef.current;
        if (!v) return;
        const current = v.currentTime;
        const dur = v.duration || 0;
        setVideoState((p) => ({
            ...p,
            currentTime: current,
            duration: dur,
            progress: dur ? (current / dur) * 100 : 0
        }));
    }, []);

    const handleLoaded = useCallback(() => {
        const v = videoRef.current;
        if (!v) return;
        const duration = v.duration;
        setVideoState((p) => ({ ...p, duration: duration }));
        trackEvent('video_metadata_loaded', { duration: duration });
    }, [trackEvent]);

    const handleEnded = useCallback(() => {
        setVideoState((p) => ({ ...p, isPlaying: false }));
        trackEvent('video_completed');
        flushOnStop({ trigger: 'ended', completed: true }).catch(() => { });
    }, [flushOnStop, trackEvent]);

    const isMobile = () => /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent);

    const handleActionClick = useCallback((action: VideoCampaignAction) => {
        trackEvent('action_button_clicked', { actionId: action.VideoCampaignActionDetailID, actionType: action.ActionType });
        flushOnStop({ trigger: 'action_clicked', actionId: action.VideoCampaignActionDetailID, actionType: action.ActionType });

        if (action.ActionType === 120000000) { // Call
            const cleanUrl = (action.ActionURL ?? '').replace(/^https?:\/\//, '');
            if (isMobile()) window.open(`tel:${cleanUrl}`, '_self');
            else alert(`Call: ${cleanUrl}`);
        } else if (action.ActionType === 120000001) { // Email
            const mailto = `mailto:${action.ActionURL}?subject=${encodeURIComponent(action.ActionSubject || '')}&body=${encodeURIComponent(action.ActionContent || '')}`;
            window.open(mailto, '_self');
        } else { // URL
            window.open(action.ActionTarget, '_blank');
        }
    }, [flushOnStop, trackEvent]);

    const getActionIcon = (actionType: string) => {
        switch (actionType) {
            case 'call':
                return <Phone size={18} />;
            case 'email':
                return <Mail size={18} />;
            default:
                return <Monitor size={18} />;
        }
    };

    if (isLoading)
        return (
            <div className="video-loading" aria-live="polite">
                <div className="video-loading-spinner">
                    <div className="video-loading-circle" />
                    <div className="video-loading-circle2" />
                </div>
                <p className="video-loading-text">Loading video...</p>
            </div>
        );

    if (!campaign)
        return <div style={{ padding: '2rem', textAlign: 'center' }}>Video not available.</div>;

    return (
        <div
            className="video-root"
            style={{
                ['--video-bg' as any]: DEFAULT_BRAND_COLORS.background,
                ['--video-primary' as any]: DEFAULT_BRAND_COLORS.primary,
                ['--video-secondary' as any]: DEFAULT_BRAND_COLORS.secondary,
                ['--video-accent' as any]: DEFAULT_BRAND_COLORS.accent,
                ['--video-text' as any]: DEFAULT_BRAND_COLORS.text
            }}
        >
            <div className="video-gradient-bg" />
            <div className="video-main-container">
                <header className="video-header" aria-hidden={false}>
                    <div className="video-logo-placeholder" aria-hidden="true" />
                    <div>
                        <h1 id="video-title-heading" data-testid="video-title-heading"  aria-label="HealConnect Welcome" role="heading" aria-level={1} tabIndex={0} className="video-welcome">{campaign.CampaignTitle}</h1>
                        <p className="video-personal-message">{campaign.CampaignSubtitle}</p>
                    </div>
                </header>
                <div className="video-contact-actions">
                    {campaign?.Actions?.map((action: any, i: number) => (
                        <button
                            key={action.VideoCampaignActionDetailID || i}
                            type="button"
                            aria-label={action.ActionText}
                            className={`video-contact-btn ${action.ActionType === 120000001 ? 'secondary' : 'primary'}`}
                            onClick={() => handleActionClick(action)}
                            id='video-action-btn'
                        >
                            {getActionIcon(action.ActionType === 120000000 ? 'call' : action.ActionType === 120000001 ? 'email' : 'url')}
                            <span>{action.ActionText}</span>
                        </button>
                    ))}
                </div>
                <section className="video-grid">
                    <div className="video-player-column">
                        <div className="video-player-container">
                            <video
                                ref={videoRef}
                                className="video-player"
                                controls
                                playsInline
                                controlsList="nodownload noremoteplayback noplaybackrate"
                                disablePictureInPicture
                                onPlay={handlePlay}
                                onPause={handlePause}
                                onTimeUpdate={handleTimeUpdate}
                                onLoadedMetadata={handleLoaded}
                                onEnded={handleEnded}
                                aria-label={campaign.CampaignTitle}
                                role='application'
                            >
                                <source src={campaign.CampaignUrl} type="video/mp4" />
                                <p>Your browser doesn't support video playback.</p>
                            </video>
                            <div className="video-progress-bar">
                                <div
                                    className="video-progress"
                                    style={{ width: `${Math.round(videoState.progress || 0)}%` }}
                                />
                            </div>
                        </div>
                    </div>
                </section>
            </div>
        </div>
    );
};

export default VideoPlayer;